import React, { useRef, useEffect, useState } from 'react';
import { useRole } from '../context/RoleContext';
import { useChat } from '../context/ChatContext';
import { useFactCheck } from '../context/FactCheckContext';
import { Loader2, BookOpen, ArrowRight, Sparkles, ShieldCheck } from 'lucide-react';

// Format inline text (bold, italic, etc)
const formatInlineText = (text) => {
  if (!text) return null;
  
  // Handle bold (**text**) and italic (*text*)
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/);
  
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index} className="font-bold">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*') && !part.startsWith('**')) {
      return <em key={index} className="italic">{part.slice(1, -1)}</em>;
    }
    return part;
  });
};

// Enhanced markdown renderer for better legibility
const formatMessage = (content, isProfessor) => {
  if (!content) return null;
  
  // Split content into lines first to better handle structure
  const lines = content.split('\n');
  const elements = [];
  let currentParagraph = [];
  let inList = false;
  let listItems = [];
  
  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      const text = currentParagraph.join(' ').trim();
      if (text) {
        elements.push(
          <p 
            key={`p-${elements.length}`}
            className={`text-sm leading-7 mb-4 ${isProfessor ? 'text-gray-200' : 'text-gray-700'}`}
          >
            {formatInlineText(text)}
          </p>
        );
      }
      currentParagraph = [];
    }
  };
  
  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`list-${elements.length}`} className="mb-4 space-y-2">
          {listItems.map((item, i) => (
            <li 
              key={i}
              className={`text-sm leading-6 flex items-start gap-3 ${isProfessor ? 'text-gray-200' : 'text-gray-700'}`}
            >
              <span className={`mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                isProfessor ? 'bg-blue-400' : 'bg-gray-400'
              }`}></span>
              <span>{formatInlineText(item)}</span>
            </li>
          ))}
        </ul>
      );
      listItems = [];
      inList = false;
    }
  };
  
  lines.forEach((line, lineIndex) => {
    const trimmedLine = line.trim();
    
    // Empty line - flush current content
    if (!trimmedLine) {
      flushList();
      flushParagraph();
      return;
    }
    
    // Check for headers (###)
    if (trimmedLine.startsWith('###')) {
      flushList();
      flushParagraph();
      elements.push(
        <h3 
          key={`h3-${lineIndex}`}
          className={`text-base font-bold mt-6 mb-3 pb-2 border-b ${
            isProfessor ? 'text-white border-gray-700' : 'text-gray-900 border-gray-200'
          }`}
        >
          {formatInlineText(trimmedLine.replace(/^###\s*/, ''))}
        </h3>
      );
      return;
    }
    
    // Check for subheaders (##)
    if (trimmedLine.startsWith('##')) {
      flushList();
      flushParagraph();
      elements.push(
        <h2 
          key={`h2-${lineIndex}`}
          className={`text-lg font-bold mt-6 mb-3 ${isProfessor ? 'text-white' : 'text-gray-900'}`}
        >
          {formatInlineText(trimmedLine.replace(/^##\s*/, ''))}
        </h2>
      );
      return;
    }
    
    // Check for numbered sections (1. **Title**: text OR 1. Title - text)
    const numberedMatch = trimmedLine.match(/^(\d+)\.\s*(.+)/);
    if (numberedMatch) {
      flushList();
      flushParagraph();
      
      const [, num, rest] = numberedMatch;
      
      // Check if it has a bold title
      const boldTitleMatch = rest.match(/^\*\*([^*]+)\*\*[:\-–—]?\s*(.*)/s);
      // Check if it has a title with dash/colon separator
      const dashTitleMatch = rest.match(/^([^:\-–—]+)[\-–—:]\s*(.*)/s);
      
      if (boldTitleMatch) {
        const [, title, description] = boldTitleMatch;
        elements.push(
          <div key={`num-${lineIndex}`} className="mb-5">
            <div className={`flex items-start gap-3 mb-2`}>
              <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold flex-shrink-0 ${
                isProfessor ? 'bg-blue-500/30 text-blue-300' : 'bg-blue-100 text-blue-700'
              }`}>
                {num}
              </span>
              <h4 className={`text-base font-bold ${isProfessor ? 'text-white' : 'text-gray-900'}`}>
                {title}
              </h4>
            </div>
            {description && (
              <p className={`text-sm leading-7 ml-10 ${isProfessor ? 'text-gray-300' : 'text-gray-600'}`}>
                {formatInlineText(description)}
              </p>
            )}
          </div>
        );
      } else if (dashTitleMatch && dashTitleMatch[1].length < 60) {
        const [, title, description] = dashTitleMatch;
        elements.push(
          <div key={`num-${lineIndex}`} className="mb-5">
            <div className={`flex items-start gap-3 mb-2`}>
              <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold flex-shrink-0 ${
                isProfessor ? 'bg-blue-500/30 text-blue-300' : 'bg-blue-100 text-blue-700'
              }`}>
                {num}
              </span>
              <h4 className={`text-base font-bold ${isProfessor ? 'text-white' : 'text-gray-900'}`}>
                {formatInlineText(title.trim())}
              </h4>
            </div>
            {description && (
              <p className={`text-sm leading-7 ml-10 ${isProfessor ? 'text-gray-300' : 'text-gray-600'}`}>
                {formatInlineText(description)}
              </p>
            )}
          </div>
        );
      } else {
        // Simple numbered item
        elements.push(
          <div key={`num-${lineIndex}`} className="mb-4 flex items-start gap-3">
            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold flex-shrink-0 mt-0.5 ${
              isProfessor ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-100 text-gray-600'
            }`}>
              {num}
            </span>
            <p className={`text-sm leading-7 ${isProfessor ? 'text-gray-200' : 'text-gray-700'}`}>
              {formatInlineText(rest)}
            </p>
          </div>
        );
      }
      return;
    }
    
    // Check for bullet points
    if (/^[-*•]\s/.test(trimmedLine)) {
      flushParagraph();
      inList = true;
      listItems.push(trimmedLine.replace(/^[-*•]\s/, ''));
      return;
    }
    
    // Check for "Idea clave", "Nota:", etc.
    if (/^(idea clave|nota|importante|recuerda|tip):/i.test(trimmedLine)) {
      flushList();
      flushParagraph();
      elements.push(
        <div 
          key={`note-${lineIndex}`}
          className={`my-4 pl-4 py-3 border-l-4 rounded-r-lg ${
            isProfessor 
              ? 'border-blue-500 bg-blue-500/10' 
              : 'border-amber-500 bg-amber-50'
          }`}
        >
          <p className={`text-sm leading-6 ${isProfessor ? 'text-gray-200' : 'text-gray-700'}`}>
            {formatInlineText(trimmedLine)}
          </p>
        </div>
      );
      return;
    }
    
    // Regular text - add to current paragraph
    if (inList) {
      flushList();
    }
    currentParagraph.push(trimmedLine);
  });
  
  // Flush remaining content
  flushList();
  flushParagraph();
  
  return elements.length > 0 ? elements : (
    <p className={`text-sm leading-7 ${isProfessor ? 'text-gray-200' : 'text-gray-700'}`}>
      {formatInlineText(content)}
    </p>
  );
};

// Single multimedia image component - 85% width with proper spacing, optimized for 14:9 images
const MultimediaImage = ({ item, isProfessor }) => {
  return (
    <div 
      className="my-8 w-full flex justify-center"
      data-testid={`multimedia-image-${item.id}`}
    >
      <div className="w-[75%]">
        <div
          className={`rounded-xl overflow-hidden ${
            isProfessor 
              ? 'bg-gray-700/30 shadow-lg shadow-black/20' 
              : 'bg-gray-100 shadow-md shadow-gray-300/50'
          }`}
        >
          <img
            src={`data:${item.mime_type};base64,${item.image_data}`}
            alt=""
            className="block w-full h-auto"
            style={{ 
              aspectRatio: '14/9',
              objectFit: 'cover'
            }}
          />
        </div>
        {item.caption && (
          <p className={`mt-3 text-xs italic text-center ${
            isProfessor ? 'text-gray-400' : 'text-gray-500'
          }`}>
            {item.caption}
          </p>
        )}
      </div>
    </div>
  );
};

// Format message with inline images positioned after relevant paragraphs
// RULE: Maximum 1 image per message
const formatMessageWithImages = (content, multimedia, isProfessor) => {
  if (!content) return null;
  
  // Get formatted elements
  const formattedElements = formatMessage(content, isProfessor);
  
  // If no multimedia or not an array of elements, return as-is
  if (!multimedia || multimedia.length === 0 || !Array.isArray(formattedElements)) {
    return formattedElements;
  }
  
  // LIMIT: Only use the first image (max 1 per message)
  const singleImage = multimedia[0];
  
  // Create a map of keywords for the single image
  const imageKeywords = [];
  const keywords = singleImage.keywords || [];
  keywords.forEach(kw => {
    imageKeywords.push(kw.toLowerCase());
  });
  if (singleImage.matched_keyword) {
    imageKeywords.push(singleImage.matched_keyword.toLowerCase());
  }
  
  // Track if image has been placed
  let imagePlaced = false;
  const result = [];
  
  // Process each formatted element
  formattedElements.forEach((element, index) => {
    result.push(element);
    
    // Only try to place if not already placed
    if (!imagePlaced) {
      // Check if this element's text contains any keyword
      const elementText = extractTextFromElement(element).toLowerCase();
      
      // Normalize text to remove accents for better matching
      const normalizedText = elementText
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      
      for (const keyword of imageKeywords) {
        const normalizedKeyword = keyword
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");
        
        // Use word boundary check with normalized text
        const pattern = new RegExp(
          `\\b${normalizedKeyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 
          'i'
        );
        
        if (pattern.test(normalizedText)) {
          // Insert image after this element
          result.push(
            <MultimediaImage 
              key={`img-${singleImage.id}-${index}`} 
              item={singleImage} 
              isProfessor={isProfessor} 
            />
          );
          imagePlaced = true;
          break;
        }
      }
    }
  });
  
  // If image wasn't placed (keyword not found), add it at the end
  if (!imagePlaced) {
    result.push(
      <MultimediaImage 
        key={`img-end-${singleImage.id}`} 
        item={singleImage} 
        isProfessor={isProfessor} 
      />
    );
  }
  
  return result;
};

// Helper to extract text content from React element
const extractTextFromElement = (element) => {
  if (typeof element === 'string') return element;
  if (!element) return '';
  if (element.props && element.props.children) {
    if (typeof element.props.children === 'string') {
      return element.props.children;
    }
    if (Array.isArray(element.props.children)) {
      return element.props.children.map(extractTextFromElement).join(' ');
    }
    return extractTextFromElement(element.props.children);
  }
  return '';
};

// Agent suggestion button component
const AgentSuggestionButton = ({ subjectId, subjectName, onNavigate }) => {
  const { isProfessor } = useRole();
  
  return (
    <button
      onClick={() => onNavigate(subjectId)}
      className={`mt-4 w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${
        isProfessor
          ? 'bg-gradient-to-r from-blue-600/30 to-violet-600/30 hover:from-blue-600/50 hover:to-violet-600/50 border border-blue-500/30 hover:border-blue-400/50'
          : 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 hover:from-amber-500/30 hover:to-orange-500/30 border border-amber-500/30 hover:border-amber-400/50'
      }`}
      data-testid={`agent-suggestion-btn-${subjectId}`}
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${
          isProfessor ? 'bg-blue-500/30' : 'bg-amber-500/30'
        }`}>
          <Sparkles className={`w-4 h-4 ${
            isProfessor ? 'text-blue-300' : 'text-amber-600'
          }`} />
        </div>
        <div className="text-left">
          <p className={`text-sm font-medium ${
            isProfessor ? 'text-white' : 'text-gray-800'
          }`}>
            Continuar con {subjectName}
          </p>
          <p className={`text-xs ${
            isProfessor ? 'text-gray-400' : 'text-gray-500'
          }`}>
            Agente especializado disponible
          </p>
        </div>
      </div>
      <ArrowRight className={`w-5 h-5 transition-transform group-hover:translate-x-1 ${
        isProfessor ? 'text-blue-400' : 'text-amber-600'
      }`} />
    </button>
  );
};

// Parse message content for agent suggestion tag
const parseAgentSuggestion = (content) => {
  if (!content) return { cleanContent: content, suggestion: null };
  
  // Match [SUGGEST_AGENT:subject_id:Subject Name]
  const regex = /\[SUGGEST_AGENT:([^:]+):([^\]]+)\]/;
  const match = content.match(regex);
  
  if (match) {
    const cleanContent = content.replace(regex, '').trim();
    return {
      cleanContent,
      suggestion: {
        subjectId: match[1],
        subjectName: match[2]
      }
    };
  }
  
  return { cleanContent: content, suggestion: null };
};

// Mini Progress Bar Component for Fact Check
const FactCheckProgressBar = ({ isProfessor }) => {
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    // Animate progress from 0 to 100 over ~2 seconds
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 2;
      });
    }, 40);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div 
      className={`inline-flex items-center gap-2 mt-3 px-2 py-1.5 rounded-lg text-xs ${
        isProfessor 
          ? 'bg-gray-700/50 text-gray-400' 
          : 'bg-gray-100 text-gray-500'
      }`}
      data-testid="fact-check-checking"
    >
      <ShieldCheck className="w-3 h-3 opacity-50" />
      <div className={`w-16 h-1.5 rounded-full overflow-hidden ${
        isProfessor ? 'bg-gray-600' : 'bg-gray-200'
      }`}>
        <div 
          className={`h-full rounded-full transition-all duration-100 ${
            isProfessor ? 'bg-blue-400' : 'bg-blue-500'
          }`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

// Fact Check Status Indicator Component
const FactCheckIndicator = ({ messageId, subjectId, question, answer, isProfessor }) => {
  const { isEnabled, checkMessage, getResult, isChecking } = useFactCheck();
  const hasTriggeredRef = React.useRef(false);
  const [showProgress, setShowProgress] = useState(false);
  
  // Trigger fact check 1 second after component mounts
  useEffect(() => {
    if (isEnabled && !hasTriggeredRef.current && messageId && subjectId && question && answer) {
      // Show progress bar immediately
      setShowProgress(true);
      
      // Trigger actual check after 1 second delay
      const timer = setTimeout(() => {
        hasTriggeredRef.current = true;
        checkMessage(messageId, subjectId, question, answer);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [isEnabled, messageId, subjectId, question, answer, checkMessage]);
  
  // Don't render if fact check is disabled
  if (!isEnabled) return null;
  
  const result = getResult(messageId);
  const checking = isChecking(messageId);
  
  // Render progress bar while checking
  if (showProgress && (checking || !result)) {
    return <FactCheckProgressBar isProfessor={isProfessor} />;
  }
  
  // Don't render if no result yet
  if (!result) return null;
  
  // Skip rendering for skipped messages (like default agent)
  if (result.status === 'skipped') return null;
  
  // Only show badge if verified - don't show anything if not verified
  // Backend already applies strict verification (70% confidence + uses_rag_data)
  const isVerified = result.is_verified;
  
  // If not verified, don't show anything (no badge, no X)
  if (!isVerified) return null;
  
  // Render verified badge with tooltip
  return (
    <div 
      className={`inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-lg text-xs cursor-default transition-all duration-300 ${
        isProfessor 
          ? 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-cyan-300 border border-cyan-500/30 hover:border-cyan-400/50' 
          : 'bg-gradient-to-r from-blue-50 to-cyan-50 text-cyan-700 border border-cyan-200 hover:border-cyan-300'
      }`}
      data-testid="fact-check-verified"
      title="Respuesta con doble verificación al Documento de Conocimientos"
    >
      <ShieldCheck className="w-3.5 h-3.5" />
      <span className="font-medium">Fact Check™</span>
    </div>
  );
};

const MessageBubble = ({ message, isProfessor, onNavigateToAgent, subjectId, previousUserMessage }) => {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const hasMultimedia = message.multimedia && message.multimedia.length > 0;
  
  // Check if message is new (created in current session - within last 5 minutes)
  // Use useMemo to memoize the result based on message.created_at
  const isNewMessage = React.useMemo(() => {
    if (!message.created_at) return false;
    const messageTime = new Date(message.created_at);
    const currentTime = new Date();
    const fiveMinutesAgo = new Date(currentTime.getTime() - 5 * 60 * 1000);
    return messageTime > fiveMinutesAgo;
  }, [message.created_at]);
  
  // Parse for agent suggestion if assistant message
  const { cleanContent, suggestion } = isAssistant 
    ? parseAgentSuggestion(message.content) 
    : { cleanContent: message.content, suggestion: null };
  
  return (
    <div 
      className={`flex mb-4 ${isUser ? 'justify-end' : 'justify-start'}`}
      data-testid={`message-${message.role}`}
    >
      {/* Message Content */}
      <div className={`max-w-[85%] rounded-2xl px-5 py-4 ${
        isUser
          ? isProfessor 
            ? 'bg-blue-500 text-white' 
            : 'bg-gray-700 text-white'
          : isProfessor
            ? 'bg-gray-800/80 text-gray-100 border border-gray-700/50'
            : 'bg-white text-gray-800 shadow-sm border border-gray-100'
      }`}>
        {isAssistant ? (
          <div className="max-w-none">
            {hasMultimedia 
              ? formatMessageWithImages(cleanContent, message.multimedia, isProfessor)
              : formatMessage(cleanContent, isProfessor)
            }
            {/* Agent Suggestion Button */}
            {suggestion && onNavigateToAgent && (
              <AgentSuggestionButton
                subjectId={suggestion.subjectId}
                subjectName={suggestion.subjectName}
                onNavigate={onNavigateToAgent}
              />
            )}
            {/* Fact Check Indicator - Only for new messages */}
            {isNewMessage && previousUserMessage && (
              <FactCheckIndicator
                messageId={message.id}
                subjectId={subjectId}
                question={previousUserMessage}
                answer={cleanContent}
                isProfessor={isProfessor}
              />
            )}
          </div>
        ) : (
          <p className="text-sm">{message.content}</p>
        )}
      </div>
    </div>
  );
};

const ChatView = () => {
  const { isProfessor } = useRole();
  const { messages, isLoading, isStreaming, error, selectedTopic, showTopicSummary, clearSelectedTopic, subjects, setSelectedSubject, startNewChat, selectedSubject } = useChat();
  const messagesEndRef = useRef(null);
  const prevMessageCountRef = useRef(0);
  const wasStreamingRef = useRef(false);
  
  // Handle navigation to a different agent
  const handleNavigateToAgent = (subjectId) => {
    // Find the subject by ID
    const targetSubject = subjects.find(s => s.id === subjectId);
    if (targetSubject) {
      // Change to the new subject and start a fresh chat
      setSelectedSubject(targetSubject);
      startNewChat();
    }
  };
  
  // Helper to get the previous user message for a given assistant message
  const getPreviousUserMessage = (index) => {
    for (let i = index - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        return messages[i].content;
      }
    }
    return null;
  };
  
  // Auto-scroll only when:
  // 1. A new message is added (user sends a message)
  // 2. Streaming just finished (wasStreaming -> !isStreaming)
  // Do NOT scroll during streaming to allow comfortable reading
  useEffect(() => {
    const messageCount = messages.length;
    const isNewMessage = messageCount > prevMessageCountRef.current;
    const streamingJustFinished = wasStreamingRef.current && !isStreaming;
    
    // Only scroll for new user messages, not during streaming updates
    if (isNewMessage && !isStreaming) {
      // Check if the new message is from the user (not assistant streaming)
      const lastMessage = messages[messageCount - 1];
      if (lastMessage?.role === 'user') {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
    
    // Scroll when streaming finishes
    if (streamingJustFinished) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    
    prevMessageCountRef.current = messageCount;
    wasStreamingRef.current = isStreaming;
  }, [messages, isStreaming]);

  // Handle showing the topic summary
  const handleShowSummary = () => {
    if (selectedTopic) {
      showTopicSummary(selectedTopic.id, selectedTopic.title);
      clearSelectedTopic();
    }
  };
  
  if (messages.length === 0) {
    return null; // Show welcome screen instead
  }
  
  return (
    <div 
      className="flex-1 overflow-y-auto px-4 py-4 min-h-0 flex flex-col"
      data-testid="chat-view"
    >
      {/* Topic Summary Button - Discretely at the top */}
      {selectedTopic && (
        <div className="flex justify-center mb-3">
          <button
            onClick={handleShowSummary}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border transition-all ${
              isProfessor
                ? 'bg-blue-500/10 border-blue-500/30 text-blue-300 hover:bg-blue-500/20'
                : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'
            }`}
            data-testid="show-summary-btn"
          >
            <BookOpen className="w-3 h-3" />
            <span>Leer resumen sobre &ldquo;{selectedTopic.title}&rdquo;</span>
          </button>
        </div>
      )}

      {/* Messages */}
      {messages.map((message, index) => (
        <MessageBubble 
          key={message.id} 
          message={message} 
          isProfessor={isProfessor}
          onNavigateToAgent={handleNavigateToAgent}
          subjectId={selectedSubject?.id}
          previousUserMessage={message.role === 'assistant' ? getPreviousUserMessage(index) : null}
        />
      ))}
      
      {/* Loading indicator */}
      {isLoading && (
        <div className="flex justify-start mb-4">
          <div className={`rounded-2xl px-4 py-3 ${
            isProfessor 
              ? 'bg-gray-800 border border-gray-700' 
              : 'bg-white shadow-sm border border-gray-100'
          }`}>
            <Loader2 className={`w-5 h-5 animate-spin ${
              isProfessor ? 'text-gray-400' : 'text-gray-500'
            }`} />
          </div>
        </div>
      )}
      
      {/* Streaming indicator */}
      {isStreaming && messages.length > 0 && messages[messages.length - 1].role === 'assistant' && (
        <div className={`text-xs -mt-2 mb-4 ${
          isProfessor ? 'text-gray-500' : 'text-gray-400'
        }`}>
          <span className="inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
            Escribiendo...
          </span>
        </div>
      )}
      
      {/* Error message */}
      {error && (
        <div className="mx-4 mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}
      
      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatView;
